import React from 'react';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../types';
import styles from './compareShareBookmark.scss';
import FragmentPlaceholder from '../../fragmentPlaceholder/fragmentPlaceholder';
import CompareBtn from './compareBtn';
import ShareIcon from '../../icons/shareIcon';
import LogoService from '../../icons/logoservice';
import TabNavigationHelper from '../../../helper/tabNavigationHelper';
import classNames from 'classnames';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
function CompareShareBookmarkButton(props: ICompareShareBookmarkButton) {
    const tabNavigation: TabNavigationHelper = TabNavigationHelper.instance;
    const { icon, label, onClick } = props;
    return (
        <button onClick={onClick} title={label} ref={(el) => tabNavigation.setFocusAtt(el)}>
            {icon}
            <span>{label}</span>
        </button>
    );
}

interface ICompareShareBookmarkButton {
    icon: React.ReactElement;
    label: string;
    onClick(): void;
}

export function CompareShareBookmarkButtonContainer(props: {
    children: JSX.Element;['data-testid']?: string,
    articleSoldOut?: boolean, title?: string
}) {
    const [childHasButton, setChildHasButton] = React.useState(false);
    const containerRef = React.useRef<HTMLElement>();

    React.useEffect(() => {
        // search for fragment button and set flag
        if (containerRef.current) {
            let ref = containerRef.current;
            let searchingForButton = true;
            while (searchingForButton) {
                if (ref.firstElementChild) {
                    if (ref.firstElementChild?.tagName === 'BUTTON') {
                        searchingForButton = false;
                        setChildHasButton(true);
                    } else {
                        ref = ref.firstElementChild as HTMLElement;
                    }
                } else {
                    searchingForButton = false;
                    setChildHasButton(false);
                }
            }
        }
    }, []);

    return (
        <div
            title={props.title}
            data-testid={props['data-testid']}
            data-semantic-id={props['data-testid']}
            style={{ display: childHasButton ? 'flex' : 'none' }}
            ref={containerRef as React.LegacyRef<HTMLDivElement>}
            className={classNames(styles.compareShareBookmark_button_container,
                { [styles.article_sold_out]: props.articleSoldOut }
            )}
        >
            {props.children}
        </div>
    );
}

const CompareShareBookmark = inject('store')(
    observer(({ store }: IInjectedProps) => {
        const seoSlug = store?.dataStore.breadcrumb.category.seoSlug ?? '';
        const savKey = store?.dataStore.orderModule.articleInfo.salesArticleVariantKey ?? '';
        const mpcItemOrigin = store?.orderModuleStore.mpcItemOrigin ?? '';


        const generalL10n = store!.localization.generalL10n;
        const logoCreatorL10n = store!.localization.logoCreatorL10n;
        function handleShareClick() {
            const shareUrl = window.location.href.split('?')[0];
            const shareTitle = generalL10n.shareLinkText.replace(
                '{0}', store?.dataStore.orderModule.articleInfo.title ?? ''
            );
            const shareText = generalL10n.shareLinkBody
                .replace('{0}', store?.dataStore.orderModule.articleInfo.title ?? '')
                .replace('{1}', generalL10n.companyNameSimple)
                + ' '
                + shareUrl;
            const shareDate: ShareData = {
                title: shareTitle,
                text: shareText,
                url: shareUrl,
            }

            if (navigator.share) {
                navigator.share(shareDate);
            } else {
                window.location.href =
                    'mailto:?subject=' +
                    encodeURIComponent(shareTitle) +
                    '&body=' +
                    encodeURIComponent(shareText);
            }
        }

        return (
            <div data-testid='compareShareBookmark-desktop-container'
              data-semantic-id='compareShareBookmark-desktop-container' className={styles.compareShareBookmark_container}>
                {store?.dataStore?.contextData?.portalMandantToken != 'GLB' && (
                    <CompareShareBookmarkButtonContainer data-testid='bookmark-button' data-semantic-id='bookmark-button'
                        articleSoldOut={store?.articleSoldOut}>
                        <FragmentPlaceholder
                            src={`/ESPP1-Bookmark/bookmark-add-button?containerId=${store?.getBookmarkContainerID}`}
                            ignoreErrors={true}
                            timeout={2}
                        />
                    </CompareShareBookmarkButtonContainer>
                )}
                {!store?.showKioskLayout && (
                    <CompareShareBookmarkButtonContainer data-testid='share-button'
                      data-semantic-id='share-button' title={generalL10n.shareButtonLocale}
                        articleSoldOut={store?.articleSoldOut}>
                        <CompareShareBookmarkButton
                            icon={<ShareIcon />}
                            label={generalL10n.shareButtonLocale}
                            onClick={handleShareClick}

                        />
                    </CompareShareBookmarkButtonContainer>
                )}
                <CompareShareBookmarkButtonContainer data-testid='compare-button' data-semantic-id='compare-button'
                    articleSoldOut={store?.articleSoldOut}>
                    <CompareBtn
                        savKey={savKey}
                        seoSlug={seoSlug}
                        itemOrigin={mpcItemOrigin}
                        data-testid='mpc-button' data-semantic-id='mpc-button'
                    />
                </CompareShareBookmarkButtonContainer>

                {/* smake variant is maintained and ft is on */}
                {store?.dataStore.contextData.featureToggles.enableLogoCreator
                    && store?.dataStore.shutterModule.productInfo?.description?.isSmakeDesignerAvailable
                    && !store.showKioskLayout && (
                        <CompareShareBookmarkButtonContainer data-testid='logo-creator-button'
                          data-semantic-id='logo-creator-button'
                            articleSoldOut={store?.articleSoldOut}>
                            <CompareShareBookmarkButton
                                icon={<LogoService />}
                                label={logoCreatorL10n!.personalise}
                                onClick={() => { store.showPersonalizeOverlay(true); }}
                            />
                        </CompareShareBookmarkButtonContainer>
                    )}
                {/* smake variant is not maintained but ft is on */}
                {store?.dataStore.contextData.featureToggles.enableLogoCreator
                    && store.dataStore.bannerModule.logoServiceRedirectUrl
                    && !store?.dataStore.shutterModule.productInfo?.description?.isSmakeDesignerAvailable &&
                    !store.showKioskLayout && (
                        <CompareShareBookmarkButtonContainer data-testid='no-logo-creator-button'
                          data-semantic-id='no-logo-creator-button'
                            articleSoldOut={store?.articleSoldOut}>
                            <CompareShareBookmarkButton
                                icon={<LogoService />}
                                label={logoCreatorL10n!.personalise}
                                onClick={() => {
                                    store?.fetchedData.shutterModule.productInfo.description.links.logoServiceInfoUrl
                                        && window.open(
                                            store?.fetchedData.shutterModule
                                                .productInfo.description.links.logoServiceInfoUrl,
                                            '_self');
                                }}
                            />
                        </CompareShareBookmarkButtonContainer>
                    )}
            </div>
        );
    })
);

export default CompareShareBookmark;

